import { IHolding } from "../../holding"
import { hasAccessPermission } from "../../session/utils"
import { RoleId } from "../../user"
import { AccessPermission, HoldingUserPermissions, ReadWriteFinancialDataAccessPermissions, ReadWritePermissions, UserPermissions } from "../types"
import { mergeHoldingSprayPlansPermissions } from "../user/spray"

type HubPermissions = HoldingUserPermissions["hub"]

export function getHoldingPermissions(role: RoleId, holding: IHolding, user: UserPermissions, holdingUser: HubPermissions): UserPermissions {

    // If true, then the SoilManagementPlan permission is set at holding level
    const holdingSoilManagementPlan = (role === RoleId.Agronomist) || (role === RoleId.Manager)

    user = {
        ...user,
        soilManagementPlan: holdingSoilManagementPlan
            ? holding.SoilManagementPlan
            : user.soilManagementPlan
    }

    const permissions = mergeHoldingPermissions(user, holdingUser)

    return permissions

}

function mergeHoldingPermissions(user: UserPermissions, holding: HubPermissions) {

    if (!holding) {
        return user
    }

    const {
        fields,
        soil,
        stocks,
        mapLayers,
        seedPlans,
        nutritionPlans,
        sprayPlans,
        manure,
        exports,
        reports,
        climate,
        fieldDiary,
        taskManagement,
        shed,
        production,
        planning
    } = holding

    user = {

        ...user,

        holdings: merge(user.holdings, holding.holdings),

        fields: merge(user.fields, fields),
        soil: merge(user.soil, soil),
        mapLayers: merge(user.mapLayers, mapLayers),
        stocks: mergeWithFinancialOption(user.stocks, stocks),

        seedPlans: merge(user.seedPlans, seedPlans),
        nutritionPlans: merge(user.nutritionPlans, nutritionPlans),
        sprayPlans: mergeHoldingSprayPlansPermissions(merge(user.sprayPlans, sprayPlans), sprayPlans),

        manure: merge(user.manure, manure),

        exports: mergeAccess(user.exports, exports),
        reports: mergeAccess(user.reports, reports),

        climate: merge(user.climate, climate),

        fieldDiary: merge(user.fieldDiary, fieldDiary),
        taskManagement: merge(user.taskManagement, taskManagement),

        yield: merge(user.yield, holding.yield),
        shed: mergeWithFinancialOption(user.shed, shed),
        production: merge(user.production, production),
        planning: merge(user.planning, planning),

    }

    return user

}

function mergeAccess<T extends { enabled: boolean }>(user: T, holding: AccessPermission): T {

    if (!holding) {
        return user
    }

    if (!user.enabled) {
        return user
    }

    return {
        ...user,
        enabled: Boolean(holding.enabled),
    }

}

function merge<T extends ReadWritePermissions>(user: T, holding: ReadWritePermissions): T {

    if (!holding) {
        return user
    }

    if (!hasAccessPermission(user)) {
        return user
    }

    return {
        ...user,
        read: Boolean(holding.read),
        write: Boolean(holding.write)
    }

}

function mergeWithFinancialOption<T extends ReadWriteFinancialDataAccessPermissions>(user: T, holding: ReadWriteFinancialDataAccessPermissions): T {


    let r = {
        ...merge(user, holding),
        financialData: Boolean(holding.financialData)
    }

    if (r.write) {
        r = {
            ...r,
            financialData: true
        }
    }

    return r

}
